import AuthContext from "@/context/auth";
import { Video } from "@/libs/models/video.ts";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import apiClient from "./api";

// 查詢 keys 常數
export const queryKeys = {
  guestVideos: "guestVideos",
  creator: "creator",
  explore: "explore",
  gifts: "gifts",
  followedCreators: "followedCreators",
  videos: "videos",
  streams: "streams",
  stream: "stream",
  streamSource: "streamSource",
  streamPayment: "streamPayment",
  streamLeaderboard: "streamLeaderboard",
  streamChatPresence: "streamChatPresence",
};

// Guest Videos Query

// Creator Query
export const useCreator = (creatorId: string) => {
  return useQuery({
    queryKey: [queryKeys.creator, creatorId],
    queryFn: () => apiClient.getCreator(creatorId),
    enabled: !!creatorId,
  });
};

// Follow/Unfollow Mutations
export const useFollowCreator = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (creatorId: string) => apiClient.followCreator(creatorId),
    onSuccess: (_, creatorId) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.creator, creatorId],
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.followedCreators] });
    },
  });
};

export const useUnfollowCreator = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (creatorId: string) => apiClient.unfollowCreator(creatorId),
    onSuccess: (_, creatorId) => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.creator, creatorId],
      });
      queryClient.invalidateQueries({ queryKey: [queryKeys.followedCreators] });
    },
  });
};

// Explore Query
export const useExplore = () => {
  return useQuery({
    queryKey: [queryKeys.explore],
    queryFn: () => apiClient.getExplore(),
  });
};

// 獲取單個直播資訊
export const useStream = (streamId: string | null) => {
  return useQuery({
    queryKey: [queryKeys.stream, streamId],
    queryFn: () => apiClient.getLiveStream(streamId!),
    enabled: !!streamId,
    refetchInterval: 30000, // Refetch every 30 seconds to check if stream is still live
    staleTime: 10000, // Consider data fresh for 10 seconds to reduce unnecessary refetches
  });
};

// 獲取直播  URL
export const useStreamSourceUrl = (streamId: string | undefined) => {
  return useQuery({
    queryKey: [queryKeys.streamSource, streamId],
    queryFn: () => apiClient.getStreamSourceUrl(streamId!),
    enabled: !!streamId,
    refetchOnWindowFocus: true, // Don't refetch when window regains focus
  });
};

// 獲取所有進行中的直播
export const useLiveStreams = () => {
  return useQuery({
    queryKey: [queryKeys.streams, "live"],
    queryFn: () => apiClient.getLiveStreams(),
    refetchInterval: 30000, // Refetch every 30 seconds for near real-time updates
    enabled: true,
  });
};



export const useDonateRanking = (streamId: string) => {
  return useQuery({
    queryKey: ["donate-ranking", streamId],
    queryFn: async () => {
      // Mock data for donate ranking
      return {
        data: [
          { id: "user1", name: "User 1", picture: "", total: 100 },
          { id: "user2", name: "User 2", picture: "", total: 50 },
        ],
        currentUser: { total: 20 },
      };
    },
    enabled: !!streamId,
    refetchInterval: 15000, // Refetch every 15 seconds
  });
};

// Stream leaderboard query with pagination
export const useStreamLeaderboard = (creatorId: string, params: {
  startDate?: number;
  endDate?: number;
  limit?: number;
} = {}) => {
  return useInfiniteQuery({
    queryKey: [queryKeys.streamLeaderboard, creatorId, params],
    queryFn: async ({ pageParam }) => {
      if (!creatorId) return { data: [], totalSummary: null, nextCursor: null };
      
      const response = await apiClient.getStreamLeaderboard(creatorId, {
        ...params,
        limit: params.limit || 50, // Default limit
        nextCursor: pageParam || undefined,
      });
      
      return response;
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.nextCursor || undefined;
    },
    initialPageParam: undefined,
    enabled: !!creatorId,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 15000, // Consider data fresh for 15 seconds
  });
};

// Check stream payment status
export const useStreamPayment = (streamId: string | null, enabled: boolean = true) => {
  return useQuery({
    queryKey: [queryKeys.streamPayment, streamId],
    queryFn: async () => {      
      return await apiClient.checkStreamPayment(streamId!);
    },
    enabled: !!streamId && enabled,
    refetchInterval: 30000, // Refetch every 30 seconds to check payment status
    retry: (failureCount, error) => {
      console.log(`[useStreamPayment] Retry attempt ${failureCount}, error:`, error);
      // Don't retry on timeout or auth errors
      if (error.message?.includes('timeout') || error.message?.includes('401') || error.message?.includes('403')) {
        return false;
      }
      return failureCount < 2; // Retry up to 2 times for other errors
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    staleTime: 5000, // Consider data fresh for 5 seconds
  });
};

// Stream payment purchase mutation
export const useStreamPaymentPurchase = (streamId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (streamId: string) => 
      apiClient.purchaseStreamTicket(streamId),
    onSuccess: (data, variables) => {
      // Invalidate payment status to refetch after successful purchase
      queryClient.invalidateQueries({
        queryKey: [queryKeys.streamPayment, variables],
      });
    },
    onError: (error) => {
      console.error('Stream payment purchase failed:', error);
    }
  });
};

export const useCheckStreamChatPresence = (streamId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: [queryKeys.streamChatPresence, streamId],
    queryFn: () => apiClient.postStreamChatPresence(streamId!),
    enabled: !!streamId && enabled,
  });
};

// Videos Feed Query
interface VideoFeedResponse {
  data: Array<{
    id: string;
    creator: {
      id: string;
    };
  }>;
  paginator: {
    next: string | null;
  };
}

// Creator Videos Query
export const useCreatorVideos = (
  creatorId: string,
  initialVideoId?: string,
) => {
  return useInfiniteQuery({
    queryKey: [queryKeys.creator, creatorId, "videos"],
    queryFn: ({ pageParam }) =>
      apiClient.getCreatorVideos(creatorId, pageParam as string),
    getNextPageParam: (lastPage) => {
      if (lastPage?.data?.length && lastPage.paginator?.next !== null) {
        return lastPage.data[lastPage.data.length - 1].id;
      }
      return undefined;
    },
    initialPageParam: initialVideoId || null,
    enabled: !!creatorId && creatorId.length >= 15,
    refetchOnWindowFocus: false,
  });
};

export const useVideosFeed = (currentVideoId?: string, enabled = true) => {
  const { user } = useContext(AuthContext);
  const [videos, setVideos] = useState<Video[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  const fetchVideos = async (cursor?: string) => {
    if (isLoading) return;
    if (!enabled) return;
    setIsLoading(true);
    try {
      const response: VideoFeedResponse = await (user
        ? apiClient.getVideosFeed(cursor)
        : apiClient.getGuestVideos(cursor));

      const newVideos = response.data.map((video) => ({
        ...video,
        creatorId: video.creatorId ?? video.creator.id,
      }));

      setVideos((prev) => [...prev, ...newVideos]);
      console.log(response);
      setNextCursor(
        response.data.length
          ? response.data[response.data.length - 1].id
          : null,
      );
      if (!isInitialized) {
        setIsInitialized(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isInitialized && !isLoading) {
      fetchVideos(currentVideoId ? currentVideoId : undefined);
    }
  }, [user, isInitialized]);

  return {
    videos,
    currentIndex: currentVideoId
      ? videos.findIndex((v) => v.id === currentVideoId)
      : 0,
    hasMore: !!nextCursor,
    isLoading,
    loadMore: () => fetchVideos(nextCursor),
  };
};
